import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Bug, MessageCircle, Heart } from "lucide-react";

const BIRTH_DATE = new Date(2017, 3, 19); // April 19, 2017

function getAgeFrom(date) {
  const today = new Date();

  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }

  return age;
}

export default function PrivacyPolicyScreen() {
  const nav = useNavigate();

  return (
    <div
      className="relative w-full h-dvh min-h-screen arcade-bg overflow-auto"
      data-testid="support-screen"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0C10]/70 border-b border-white/5 safe-top">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            className="hud-pill hud-pill--cyan"
            onClick={() => nav(-1)}
            data-testid="support-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-sm">
              {" "}Back
            </span>
          </button>

          <h1 className="font-display font-black text-xl sm:text-2xl neon-cyan">
            SUPPORT
          </h1>

          <div className="w-20" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-5 py-6 sm:px-8 sm:py-10 text-white/80 leading-relaxed space-y-8">

        {/* Intro */}
        <div className="text-center space-y-3">
          <p className="text-2xl font-display font-black text-white">
            Need Help?
          </p>

          <p className="text-slate-300">
            Thanks for playing{" "}
            <span className="font-semibold text-white">
              {" "}Crossbeau Chaos
            </span>
            {" "}!
          </p>

          <p className="text-sm text-slate-400 max-w-xl mx-auto">
            If something isn’t working, you found a bug, or you want to share feedback,
            tap one of the options below to send us an email directly.
          </p>
        </div>

        {/* About */}
        <Section title="About">
          <p>
            Crossbeau Chaos was created by{" "}
            <span className="font-semibold text-white">
              {" "}Beau
            </span>
            {", a "}
            {getAgeFrom(BIRTH_DATE)}
            {"-year-old game creator, together with his dad."}
          </p>

          <p>
            The game is built with creativity, learning, and a love for chaotic arcade fun.
          </p>
        </Section>

        {/* Actions */}
        <Section title="Support">
          <div className="space-y-3">

            <SupportLink
              icon={<Bug className="w-4 h-4" />}
              text="Report bugs or crashes"
              href="mailto:ruben.cagnie@gmail.com?subject=Report%20bugs%20or%20crashes%20-%20Crossbeau%20Chaos"
            />

            <SupportLink
              icon={<MessageCircle className="w-4 h-4" />}
              text="Send feedback or ideas"
              href="mailto:ruben.cagnie@gmail.com?subject=Feedback%20or%20ideas%20-%20Crossbeau%20Chaos"
            />

            <SupportLink
              icon={<Heart className="w-4 h-4" />}
              text="Share something you loved"
              href="mailto:ruben.cagnie@gmail.com?subject=I%20loved%20this%20part%20-%20Crossbeau%20Chaos"
            />

          </div>
        </Section>

        {/* Footer */}
        <div className="pt-4 text-center text-slate-500 text-sm">
          {" "}We read every message 💖
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display font-black text-lg tracking-wider neon-pink">
        {" "}{title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function SupportLink({ icon, text, href }) {
  return (
    <a
      href={href}
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/10 active:scale-[0.98] transition"
    >
      <div className="text-cyan-300">{icon}</div>
      <span className="text-white/80 font-medium">
        {" "}{text}
      </span>
    </a>
  );
}